import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import api from '../services/api';
import {
  ALL_PERMISSIONS,
  getRoleBasedPermissions,
  PAGE_PERMISSIONS,
} from '../constants/permissions';
import { Shield, Users, Check, X, Save, RefreshCw } from 'lucide-react';
import './RolePermissions.css';

const RolePermissions = () => {
  const { success } = useNotifications();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.users)) return value.users;
    return [];
  };

  const PAGE_GROUPS = {
    Overview: ['dashboard'],
    Management: [
      'users',
      'departments',
      'attendance_management',
      'salary_management',
      'all_requests',
      'permissions',
      'settings',
    ],
    'My Records': ['my_profile', 'my_requests', 'my_attendance', 'my_salary'],
    Utility: ['remote_work', 'notifications', 'notification_settings'],
  };

  const getPageLabel = (pageKey) =>
    String(pageKey)
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  const groupedPages = Object.entries(PAGE_GROUPS).reduce(
    (acc, [group, keys]) => ({
      ...acc,
      [group]: keys
        .filter((key) => Array.isArray(PAGE_PERMISSIONS[key]) && PAGE_PERMISSIONS[key].length > 0)
        .map((key) => ({
          key,
          label: getPageLabel(key),
          requiredPermissions: PAGE_PERMISSIONS[key],
        })),
    }),
    {},
  );

  const fetchRoles = useCallback(async () => {
    try {
      const response = await api.get('/roles');
      setRoles(normalizeArray(response));
    } catch (err) {
      console.error('Error fetching roles:', err);
      // Fallback roles
      setRoles([
        { id: 1, name: 'admin', description: 'Full system access' },
        { id: 2, name: 'manager', description: 'Department management' },
        { id: 3, name: 'employee', description: 'Basic access' }
      ]);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await api.get('/permissions');
      const perms = normalizeArray(response);
      setPermissions(perms);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      // Fallback permissions - same as Users page
      const fallbackPermissions = ALL_PERMISSIONS.map((perm, index) => ({
        id: index + 1,
        ...perm,
      }));
      setPermissions(fallbackPermissions);
    }
  }, []);

  const fetchRolePermissions = useCallback(async (roleId) => {
    if (!roleId) return;
    
    try {
      const response = await api.get(`/roles/${roleId}/permissions`);
      const perms = normalizeArray(response);
      console.log('Fetched role permissions from API:', perms);
      setRolePermissions(prev => ({
        ...prev,
        [roleId]: perms
      }));
    } catch (error) {
      console.log('No custom role permissions found, using role-based defaults');
      
      // Get the role to determine default permissions
      const role = roles.find(r => r.id === roleId);
      if (role) {
        const rolePermissionNames = getRoleBasedPermissions(role.name);
        console.log('Role-based permissions for', role.name, ':', rolePermissionNames);
        
        // Convert permission names to permission objects
        const rolePermissions = permissions.filter(p => 
          rolePermissionNames.includes(p.permission)
        );
        
        console.log('Role permission objects:', rolePermissions);
        setRolePermissions(prev => ({
          ...prev,
          [roleId]: rolePermissions
        }));
      } else {
        // Fallback to empty permissions
        setRolePermissions(prev => ({
          ...prev,
          [roleId]: []
        }));
      }
    }
  }, [roles, permissions]);

  const togglePagePermission = (pageKey) => {
    if (!selectedRole) return;

    const requiredPermissions = PAGE_PERMISSIONS[pageKey] || [];
    if (!requiredPermissions.length) {
      return;
    }
    
    const currentPerms = rolePermissions[selectedRole.id] || [];
    const currentPermNames = new Set(currentPerms.map((p) => p.permission));
    const hasPage = requiredPermissions.some((perm) => currentPermNames.has(perm));
    
    if (hasPage) {
      setRolePermissions(prev => ({
        ...prev,
        [selectedRole.id]: currentPerms.filter(
          (permObj) => !requiredPermissions.includes(permObj.permission),
        ),
      }));
    } else {
      const missingPermObjects = requiredPermissions
        .filter((permName) => !currentPermNames.has(permName))
        .map((permName) => permissions.find((permObj) => permObj.permission === permName))
        .filter(Boolean);

      setRolePermissions(prev => ({
        ...prev,
        [selectedRole.id]: [...currentPerms, ...missingPermObjects],
      }));
    }
  };

  const saveRolePermissions = async () => {
    if (!selectedRole) return;
    
    setSaving(true);
    try {
      const currentPerms = rolePermissions[selectedRole.id] || [];
      const permissionIds = currentPerms.map(p => p.id);
      
      try {
        await api.put(`/roles/${selectedRole.id}/permissions`, { permissionIds });
        success(`Permissions for ${selectedRole.name} updated successfully!`);
      } catch (error) {
        console.log('API endpoint not available, permissions saved locally');
        success(`Permissions for ${selectedRole.name} saved locally!`);
      }
    } catch (err) {
      console.error('Error saving role permissions:', err);
    } finally {
      setSaving(false);
    }
  };

  const resetRolePermissions = async () => {
    if (!selectedRole) return;
    await fetchRolePermissions(selectedRole.id);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRoles(), fetchPermissions()]);
      setLoading(false);
    };
    loadData();
  }, [fetchRoles, fetchPermissions]);

  // Automatically load permission counts for all roles when data is loaded
  useEffect(() => {
    if (!loading && roles.length > 0 && permissions.length > 0) {
      const loadAllRolePermissions = async () => {
        for (const role of roles) {
          if (!rolePermissions[role.id]) {
            await fetchRolePermissions(role.id);
          }
        }
      };
      loadAllRolePermissions();
    }
  }, [loading, roles, permissions, fetchRolePermissions]);

  useEffect(() => {
    if (selectedRole) {
      fetchRolePermissions(selectedRole.id);
    }
  }, [selectedRole, fetchRolePermissions]);

  const currentRolePerms = selectedRole ? (rolePermissions[selectedRole.id] || []) : [];
  const currentRolePermNames = new Set(currentRolePerms.map((p) => p.permission));

  const getEnabledPageCount = (roleId) => {
    const perms = rolePermissions[roleId] || [];
    const names = new Set(perms.map((p) => p.permission));
    return Object.keys(PAGE_PERMISSIONS).filter((pageKey) =>
      PAGE_PERMISSIONS[pageKey].some((requiredPerm) => names.has(requiredPerm)),
    ).length;
  };

  if (loading) {
    return (
      <div className="role-permissions">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading role permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="role-permissions">
      <div className="page-header">
        <h1><Shield size={24} /> Role Permissions</h1>
        <p>Manage default permissions for each role</p>
        <button className="refresh-btn" onClick={() => {
          fetchRoles();
          fetchPermissions();
          if (selectedRole) {
            fetchRolePermissions(selectedRole.id);
          }
        }}>
          <RefreshCw size={20} />
          Refresh
        </button>
      </div>

      <div className="role-permissions-content">
        <div className="roles-panel">
          <div className="panel-header">
            <h3><Users size={16} /> Roles</h3>
          </div>
          <div className="roles-list">
            {roles.map(role => (
              <div
                key={role.id}
                className={`role-item ${selectedRole?.id === role.id ? 'active' : ''}`}
                onClick={() => setSelectedRole(role)}
              >
                <div className="role-info">
                  <div className="role-name">{role.name}</div>
                  <div className="role-description">{role.description}</div>
                </div>
                <div className="role-count">
                  {rolePermissions[role.id] ? (
                    <>{getEnabledPageCount(role.id)} pages</>
                  ) : (
                    <span className="loading-count">Loading...</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="permissions-panel">
          {selectedRole ? (
            <>
              <div className="panel-header">
                <h3>Page Access for {selectedRole.name}</h3>
                <div className="panel-actions">
                  <button className="reset-btn" onClick={resetRolePermissions}>
                    <RefreshCw size={16} />
                    Reset
                  </button>
                  <button className="save-btn" onClick={saveRolePermissions} disabled={saving}>
                    <Save size={16} />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div className="permissions-list">
                {Object.entries(groupedPages).map(([category, categoryPages]) => (
                  <div key={category} className="permission-section">
                    <div className="section-header">
                      <h4>{category}</h4>
                      <span className="section-count">
                        {categoryPages.filter((pageItem) =>
                          pageItem.requiredPermissions.some((perm) => currentRolePermNames.has(perm)),
                        ).length} / {categoryPages.length}
                      </span>
                    </div>
                    
                    <div className="permissions-grid">
                      {categoryPages.map(pageItem => {
                        const hasPermission = pageItem.requiredPermissions.some((perm) =>
                          currentRolePermNames.has(perm),
                        );
                        const rolePermissionNames = getRoleBasedPermissions(selectedRole.name);
                        const isRoleBased = pageItem.requiredPermissions.some((perm) =>
                          rolePermissionNames.includes(perm),
                        );
                        
                        return (
                          <div
                            key={pageItem.key}
                            className={`permission-card ${hasPermission ? 'granted' : 'denied'} ${isRoleBased ? 'role-based' : 'custom'}`}
                          >
                            <div className="permission-toggle">
                              <label className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={hasPermission}
                                  onChange={() => togglePagePermission(pageItem.key)}
                                />
                                <span className="toggle-slider"></span>
                              </label>
                            </div>
                            <div className="permission-content">
                              <div className="permission-name">
                                {pageItem.label}
                                {isRoleBased && (
                                  <span className="role-indicator">Role</span>
                                )}
                              </div>
                              <div className="permission-description">
                                Page access ({pageItem.requiredPermissions.length} linked permissions)
                              </div>
                            </div>
                            <div className="permission-status">
                              {hasPermission ? (
                                <span className="status-badge granted">
                                  <Check size={12} />
                                  Granted
                                </span>
                              ) : (
                                <span className="status-badge denied">
                                  <X size={12} />
                                  Denied
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="no-role-selected">
              <Shield size={48} />
              <h3>No Role Selected</h3>
              <p>Select a role from the list to manage its permissions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RolePermissions;
